import type { RequiringAction, RequiringActionName } from '@paid-ui/types';
import type { ContractDocument } from '@paid-ui/types/document';

import { formatCurrency } from '@paid-ui/components/currency-input';
import { requiringActionLabelMap } from '@paid-ui/constants';
import { contractTypeTextMap } from '@paid-ui/constants/contract-type';
import { RequiringActionFeatureType, RequiringActionStatus } from '@paid-ui/enums/requiring-action';

import { nowMel, utc2Mel } from './datetime';

export const isAcknowledgedRequiringAction = (ra: RequiringAction) => {
  return ra.actionStatus === RequiringActionStatus.ACKNOWLEDGED;
};

export const isVisibleRequiringAction = (ra: RequiringAction) => {
  return ra.visibleFrom ? nowMel().isAfter(utc2Mel(ra.visibleFrom)) : true;
};

export const formatRequiringActionTitle = (ra: RequiringAction) => {
  const { action, featureItemType, data } = ra;
  const actionName = `${action}_${featureItemType}` as RequiringActionName;
  if (actionName === 'REVIEW_RETENTION_REQUEST') {
    return 'Review request to return retention funds to the payer';
  }
  const actionText = requiringActionLabelMap[actionName] ?? actionName;
  const amountText = data?.amount ? formatCurrency(Number(data.amount), { withSign: true }) : '';
  return [actionText, amountText].filter(Boolean).join(': ');
};

export const formatRequiringActionDescription = (ra: RequiringAction) => {
  if (ra.data?.preview) {
    return ra.data.preview;
  }

  let documentsText = '';
  const contractTypeText = ra.data?.contractType ? contractTypeTextMap[ra.data.contractType] : '';
  const theOtherPartyText = ra.data?.theOtherPartyName ?? '';
  const addressText = ra.data?.contractAddressLine1 ?? '';
  const dueDateText = ra?.dueDate ? 'due ' + utc2Mel(ra.dueDate).format('DD MMM YYYY') : '';

  if (ra.data?.documents) {
    const documents = JSON.parse(ra.data.documents) as ContractDocument[];
    documentsText = `Totally ${documents.length} additional documents uploaded`;
  }

  if (ra.featureItemType === RequiringActionFeatureType.BACKGROUND_CHECK) {
    // Echoes the promo this action leads to — "One application. Two accounts. Free to open
    // and no lock-ins." The old copy sold one account and called starting an application
    // "completing" it. Front-loaded, because the card variant truncates to one line.
    return 'One application opens both a Transaction and a Savings account, free with no lock-ins';
  }

  if (
    ra.featureItemType === RequiringActionFeatureType.ASSOCIATE_RECKONER_REQUEST &&
    theOtherPartyText
  ) {
    return `Sender: ${theOtherPartyText}`;
  }

  return (
    [contractTypeText, theOtherPartyText, addressText, dueDateText, documentsText]
      .filter(Boolean)
      .join(' · ') || 'No more details'
  );
};
